import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import CustomInput from "../../../components/CustomForm/CustomInput";
import CustomSelect from "../../../components/CustomForm/CustomSelect";
import CustomAlert from "../../../components/Common/CustomAlert";
import CheckRender from "../../../components/Common/CheckRender";
import {
  FormWrapper,
  Form as BaseForm,
  FormGroup as BaseFormGroup,
} from "../../../components/CustomForm/FormStyles";
import useEmpresa from "../../../hooks/useEmpresa";
import {
  dataMotivoEgreso,
  dataCausaInternamiento,
  dataTipoServicioCierreCuentas,
} from "../../../utils/LocalData";
import { toDateInputValue } from "../utils/HospitalizacionUtils";

const AutorizacionForm = styled(BaseForm)`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 28px;
  row-gap: 18px;
  align-items: end;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const AutorizacionFormGroup = styled(BaseFormGroup)`
  min-width: 0;
  width: 100%;
  
  /* Forzar que todos los inputs y selects ocupen el 100% */
  input, select {
    width: 100% !important;
    height: 37px !important;
    box-sizing: border-box;
  }
`;

const SERVICIO_AMBULATORIO = "1";
const SERVICIO_HOSPITALIZACION = "2";
const SERVICIO_MATERNIDAD = "5";
const SERVICIO_PARTO = "11";
const SERVICIO_CESAREA = "12";

const AutorizacionIngresoDetalle = ({
  encabezado,
  ingresoDetalle,
  diasHospitalarios = 3,
  diasRegistroEgresos = 5,
  validarServicioAmbulatorioData,
  validarServicioAmbulatorioLoading,
  onFormularioBloqueado,
  onFechaEgresoChange,
  onTipoServicioChange,
  motivoEgresoControlled,
  onMotivoEgresoChange,
  validarAfiliadoVigente,
  validarAfiliadoVigenteData,
  validarAfiliadoVigenteLoading,
  validarAfiliadoVigenteError,
  limpiarValidarAfiliadoVigente,
  guardarEgreso,
  cambiarServicio,
  cambiarServicioData,
  cambiarServicioLoading,
  cambiarServicioError,
  limpiarCambiarServicio,
  authUserData,
  erroresValidacion = {},
  formularioBloqueadoPorHojaQX = false,
}) => {
  const empresa = useEmpresa();

  const fechaIngresoValue = toDateInputValue(encabezado?.fechaIngreso);
  const fechaSalidaValue = toDateInputValue(encabezado?.fechaSalida);
  const motivoEgresoValue =
    encabezado?.codigoMotivoEgreso != null && encabezado?.codigoMotivoEgreso !== ""
      ? String(encabezado.codigoMotivoEgreso)
      : encabezado?.motivoEgreso || "";
  
  // Fecha máxima para el campo de fecha egreso (fecha actual)
  const fechaMaximaEgreso = new Date().toISOString().split('T')[0];
  const codigoServicioValue = encabezado?.codigoServicio != null 
    ? String(encabezado.codigoServicio) 
    : "";
  const causaInternamientoRaw = encabezado?.origenInternamineto || "";
  const isConvertido = encabezado?.estatusDesc === "CONVERTIDO";
  const isPdssNoValido = encabezado?.checkPdss === "S";
  
  // Verificar si el ingreso tiene coberturas (del backend o agregadas en memoria)
  const tieneCoberturas = Array.isArray(ingresoDetalle) && ingresoDetalle.length > 0;

  const [motivoEgresoSelected, setMotivoEgresoSelected] = useState(motivoEgresoValue);
  const [tipoServicioSelected, setTipoServicioSelected] = useState(codigoServicioValue);
  const [fechaEgresoSelected, setFechaEgresoSelected] = useState(fechaSalidaValue);
  const [formularioBloqueado, setFormularioBloqueado] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});
  const [pendingFechaEgreso, setPendingFechaEgreso] = useState(null);
  const [esperandoValidacionAfiliado, setEsperandoValidacionAfiliado] = useState(false);
  const alertaConvertidoMostradaRef = useRef(false); // Para mostrar la alerta de convertido solo una vez

  // Sincronizar Motivo Egreso cuando el encabezado trae codigoMotivoEgreso (solo en modo no controlado)
  useEffect(() => {
    if (motivoEgresoControlled === undefined) {
      setMotivoEgresoSelected(motivoEgresoValue);
    }
  }, [motivoEgresoValue, motivoEgresoControlled]);

  const currentMotivoEgreso =
    motivoEgresoControlled !== undefined ? motivoEgresoControlled : motivoEgresoSelected;
  const handleMotivoEgresoSelect = (value) => {
    if (onMotivoEgresoChange) {
      onMotivoEgresoChange(value);
    } else {
      setMotivoEgresoSelected(value);
    }
  };

  const prestadorCodigo = authUserData?.prestador?.CODIGO || authUserData?.prestador?.codigo || 0;
  const prestadorNombre = authUserData?.prestador?.nombre || authUserData?.prestador?.NOMBRE || "";
  const prestadorCorreo = authUserData?.prestador?.correoElectronico || "";

  const construirPayloadEgreso = (tipoServicio, motivoEgreso, fechaEgreso) => {
    if (!encabezado) return null;

    const poliza = encabezado?.poliza || "";
    const polizaParts = poliza.split("-");
    const compania = polizaParts.length >= 1 ? Number(polizaParts[0]) : 0;
    const ramo = polizaParts.length >= 2 ? Number(polizaParts[1]) : 0;
    const secPol = polizaParts.length >= 3 ? Number(polizaParts[2]) : 0;

    const numeroIngresoRaw = encabezado?.numeroIngreso || "";
    const secuencialMatch = numeroIngresoRaw.match(/\d+-(\d+)/);
    const anoMatch = numeroIngresoRaw.match(/^(\d{4})/);
    const ano = anoMatch ? Number(anoMatch[1]) : new Date().getFullYear();

    const fechaActual = new Date().toISOString();
    const fechaIngresoISO = encabezado?.fechaIngreso
      ? new Date(encabezado.fechaIngreso).toISOString()
      : fechaActual;
    const fechaEgresoISO = fechaEgreso
      ? new Date(`${fechaEgreso}T00:00:00`).toISOString()
      : null;

    return {
      origen: "WEBSALUD",
      ano: ano,
      compania: compania,
      ramo: ramo,
      secPol: secPol,
      plan: Number(encabezado?.planPoliza || 0),
      asegurado: Number(encabezado?.numeroAsegurado || 0),
      secuencia: 1,
      noIngreso: `ING-${numeroIngresoRaw}`,
      fecIngreso: fechaIngresoISO,
      fecEgreso: fechaEgresoISO,
      fecTra: fechaActual,
      horaTra: fechaActual,
      codMedicoIngreso: Number(prestadorCodigo),
      codDiagnosticoIngreso: "Z000",
      estatusEgreso: 1,
      codigoPss: Number(prestadorCodigo),
      tipoReclamante: encabezado?.tipoCentroMed || "",
      correoPss: prestadorCorreo,
      usuario: `pss: ${prestadorCodigo}`,
      usuUAct: `pss: ${prestadorCodigo}`,
      fecUAct: fechaActual,
      comentarioEgreso: "Egreso registrado via WS",
      observacionSpool: "Sin observaciones",
    };
  };

  const llamarGuardarEgreso = (tipoServicio, motivoEgreso, fechaEgreso) => {
    const payload = construirPayloadEgreso(tipoServicio, motivoEgreso, fechaEgreso);
    if (payload && typeof guardarEgreso === "function") {
      guardarEgreso(payload);
    }
  };

  useEffect(() => setFechaEgresoSelected(fechaSalidaValue), [fechaSalidaValue]);
  useEffect(() => setTipoServicioSelected(codigoServicioValue), [codigoServicioValue]);
  useEffect(() => {
    if (typeof onFechaEgresoChange === "function") {
      onFechaEgresoChange(fechaEgresoSelected);
    }
  }, [fechaEgresoSelected, onFechaEgresoChange]);
  useEffect(() => {
    if (typeof onTipoServicioChange === "function") {
      onTipoServicioChange(tipoServicioSelected);
    }
  }, [tipoServicioSelected, onTipoServicioChange]);
  useEffect(() => {
    if (typeof onFormularioBloqueado === "function") {
      onFormularioBloqueado(formularioBloqueado);
    }
  }, [formularioBloqueado, onFormularioBloqueado]);

  // Efecto para manejar la respuesta de cambiarServicio
  useEffect(() => {
    if (cambiarServicioData && Object.keys(cambiarServicioData).length > 0) {
      setAlertConfig({
        type: "success",
        title: "¡Éxito!",
        message: "El servicio se ha cambiado correctamente.",
        onConfirm: () => {
          setShowAlert(false);
          if (typeof limpiarCambiarServicio === "function") {
            limpiarCambiarServicio();
          }
        },
      });
      setShowAlert(true);
    }
  }, [cambiarServicioData, limpiarCambiarServicio]);

  // Efecto para manejar errores de cambiarServicio
  useEffect(() => {
    if (cambiarServicioError && Object.keys(cambiarServicioError).length > 0) {
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: cambiarServicioError?.mensaje || "Error al cambiar el servicio. Por favor intente nuevamente.",
        onConfirm: () => {
          setShowAlert(false);
          if (typeof limpiarCambiarServicio === "function") {
            limpiarCambiarServicio();
          }
        },
      });
      setShowAlert(true);
    }
  }, [cambiarServicioError, limpiarCambiarServicio]);

  // Efecto para mostrar alerta cuando el ingreso está convertido
  useEffect(() => {
    if (isConvertido && encabezado && !alertaConvertidoMostradaRef.current) {
      alertaConvertidoMostradaRef.current = true;
      setAlertConfig({
        type: "info",
        title: "¡Información!",
        message: "Cuenta cerrada: no se permite edición. Para correcciones contacte CAC.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
    }
    
    // Resetear la referencia cuando cambia el ingreso
    if (!isConvertido) {
      alertaConvertidoMostradaRef.current = false;
    }
  }, [isConvertido, encabezado]);

  // Efecto para validar servicio ambulatorio y hospitalización con fechas
  useEffect(() => {
    // No validar si el formulario ya está bloqueado por conversión
    if (isConvertido || isPdssNoValido) {
      return;
    }

    // Validar solo si tiene ambas fechas
    if (!fechaIngresoValue || !fechaSalidaValue) {
      return;
    }

    // Validación para servicio AMBULATORIO: las fechas deben ser iguales (mismo día)
    if (tipoServicioSelected === SERVICIO_AMBULATORIO) {
      if (fechaIngresoValue !== fechaSalidaValue) {
        setAlertConfig({
          type: "error",
          title: "¡Error!",
          message: "El paciente supera límites de días autorizados por este canal; gestionar por CAC/Call Center.",
          onConfirm: () => setShowAlert(false),
        });
        setShowAlert(true);
        setFormularioBloqueado(true);
      }
      return;
    }

    // Validación para servicio HOSPITALIZACIÓN: la fecha de egreso no puede ser mayor a diasHospitalarios días de la fecha de ingreso
    if (tipoServicioSelected === SERVICIO_HOSPITALIZACION) {
      const fechaIngreso = new Date(`${fechaIngresoValue}T00:00:00`);
      const fechaSalida = new Date(`${fechaSalidaValue}T00:00:00`);

      if (!isNaN(fechaIngreso.getTime()) && !isNaN(fechaSalida.getTime())) {
        const diferenciaMs = fechaSalida - fechaIngreso;
        const diferenciaDias = Math.floor(diferenciaMs / (1000 * 60 * 60 * 24));

        if (diferenciaDias > diasHospitalarios) {
          setAlertConfig({
            type: "error",
            title: "¡Error!",
            message: "El paciente supera límites de días autorizados por este canal; gestionar por CAC/Call Center.",
            onConfirm: () => setShowAlert(false),
          });
          setShowAlert(true);
          setFormularioBloqueado(true);
        }
      }
    }

    // Validación para servicio MATERNIDAD, PARTO y CESAREA: la fecha de egreso no puede ser mayor a diasHospitalarios días de la fecha de ingreso
    if (tipoServicioSelected === SERVICIO_MATERNIDAD || 
        tipoServicioSelected === SERVICIO_PARTO || 
        tipoServicioSelected === SERVICIO_CESAREA) {
      const fechaIngreso = new Date(`${fechaIngresoValue}T00:00:00`);
      const fechaSalida = new Date(`${fechaSalidaValue}T00:00:00`);

      if (!isNaN(fechaIngreso.getTime()) && !isNaN(fechaSalida.getTime())) {
        const diferenciaMs = fechaSalida - fechaIngreso;
        const diferenciaDias = Math.floor(diferenciaMs / (1000 * 60 * 60 * 24));

        if (diferenciaDias > diasHospitalarios) {
          setAlertConfig({
            type: "error",
            title: "¡Error!",
            message: "El paciente supera límites de días autorizados por este canal; gestionar por CAC/Call Center.",
            onConfirm: () => setShowAlert(false),
          });
          setShowAlert(true);
          setFormularioBloqueado(true);
        }
      }
    }
  }, [tipoServicioSelected, fechaIngresoValue, fechaSalidaValue, isConvertido, isPdssNoValido, diasHospitalarios]);

  const mostrarAlertaNoVigente = () => {
    setAlertConfig({
      type: "confirm",
      title: "¡Atención!",
      message:
        "Asegurado no se encuentra vigente a la fecha del egreso, favor contactar a nuestro Centro de Atención al Cliente, al 809-476-3535, opción 1.",
      confirmText: "Aceptar",
      cancelText: "Cancelar",
      onConfirm: () => {
        setShowAlert(false);
        setFormularioBloqueado(true);
        setFechaEgresoSelected(pendingFechaEgreso);
        setPendingFechaEgreso(null);
        if (typeof limpiarValidarAfiliadoVigente === "function") {
          limpiarValidarAfiliadoVigente();
        }
      },
      onCancel: () => {
        setShowAlert(false);
        setPendingFechaEgreso(null);
        if (typeof limpiarValidarAfiliadoVigente === "function") {
          limpiarValidarAfiliadoVigente();
        }
      },
    });
    setShowAlert(true);
  };

  useEffect(() => {
    if (!esperandoValidacionAfiliado || validarAfiliadoVigenteLoading) {
      return;
    }

    if (validarAfiliadoVigenteError) {
      setEsperandoValidacionAfiliado(false);
      mostrarAlertaNoVigente();
      return;
    }

    if (validarAfiliadoVigenteData && typeof validarAfiliadoVigenteData.estaVigente !== "undefined") {
      setEsperandoValidacionAfiliado(false);

      if (validarAfiliadoVigenteData.estaVigente === true) {
        setFechaEgresoSelected(pendingFechaEgreso);
        llamarGuardarEgreso(tipoServicioSelected, currentMotivoEgreso, pendingFechaEgreso);
        setPendingFechaEgreso(null);
        if (typeof limpiarValidarAfiliadoVigente === "function") {
          limpiarValidarAfiliadoVigente();
        }
      } else {
        mostrarAlertaNoVigente();
      }
    }
  }, [
    esperandoValidacionAfiliado,
    validarAfiliadoVigenteData,
    validarAfiliadoVigenteLoading,
    validarAfiliadoVigenteError,
    pendingFechaEgreso,
    limpiarValidarAfiliadoVigente,
  ]);

  const causaOptions = (() => {
    const base = Array.isArray(dataCausaInternamiento)
      ? dataCausaInternamiento
      : [];

    if (!causaInternamientoRaw) return base;

    const existe = base.some((opt) => {
      const v = (opt.value ?? opt.label ?? "").toString().toUpperCase();
      const raw = causaInternamientoRaw.toString().toUpperCase();
      return v === raw;
    });

    if (existe) return base;

    return [
      ...base,
      {
        value: causaInternamientoRaw,
        label: causaInternamientoRaw,
      },
    ];
  })();

  const motivoOptions = (() => {
    const base = Array.isArray(dataMotivoEgreso) ? dataMotivoEgreso : [];
    const valueToShow = currentMotivoEgreso ?? motivoEgresoValue;

    if (!valueToShow) return base;

    const existe = base.some((opt) => {
      const v = (opt.value ?? opt.label ?? "").toString().toUpperCase();
      const raw = valueToShow.toString().toUpperCase();
      return v === raw;
    });

    if (existe) return base;

    return [
      ...base,
      {
        value: valueToShow,
        label: valueToShow,
      },
    ];
  })();

  const tienePrecertificacion =
    encabezado?.numeroPrecertificacion != null &&
    encabezado?.numeroPrecertificacion !== 0;

  const esIngresoAmbulatorio =
    tienePrecertificacion &&
    !validarServicioAmbulatorioLoading &&
    validarServicioAmbulatorioData?.resultado === 0;

  const mostrarAlertaBloqueo = () => {
    setAlertConfig({
      type: "info",
      title: "Formulario Bloqueado",
      message:
        "Ha confirmado la modificación del servicio. El registro del egreso no podrá completarse en WebSalud. Por favor, gestione la alta a través de nuestro Centro de Atención al Cliente.",
      confirmText: "Entendido",
      onConfirm: () => setShowAlert(false),
    });
    setShowAlert(true);
  };

  const mostrarAlertaConfirmacion = () => {
    setAlertConfig({
      type: "confirm",
      title: "¡Atención!",
      message:
        "No es posible cambiar el servicio del ingreso desde WebSalud. Si desea modificar el servicio, debe comunicarse con nuestro Centro de Atención al Cliente.",
      confirmText: "Sí",
      cancelText: "No",
      onConfirm: () => {
        setShowAlert(false);
        setFormularioBloqueado(true);
        setTimeout(mostrarAlertaBloqueo, 100);
      },
      onCancel: () => setShowAlert(false),
    });
    setShowAlert(true);
  };

  const handleTipoServicioChange = (nuevoValor) => {
    const servicioOriginal = codigoServicioValue;
    const servicioNuevo = nuevoValor;

    // Si no hay cambio, actualizar sin validaciones
    if (servicioOriginal === servicioNuevo) {
      setTipoServicioSelected(nuevoValor);
      return;
    }

    // PRIMERA VALIDACIÓN: Si tiene coberturas, no permitir cambio de servicio desde WebSalud
    if (tieneCoberturas) {
      mostrarAlertaConfirmacion();
      return;
    }

    // Validación para servicio ambulatorio con precertificación
    const requiereValidacion =
      tienePrecertificacion &&
      esIngresoAmbulatorio &&
      nuevoValor !== codigoServicioValue;

    if (requiereValidacion) {
      mostrarAlertaConfirmacion();
      return;
    }

    // Validación: Si el servicio original es Hospitalización, mostrar confirmación
    if (servicioOriginal === SERVICIO_HOSPITALIZACION) {
      mostrarAlertaConfirmacion();
      return;
    }

    // Validar cambios de servicio desde Maternidad, Parto o Cesárea
    // Definir los servicios relacionados con maternidad
    const serviciosMaternidad = [SERVICIO_MATERNIDAD, SERVICIO_PARTO, SERVICIO_CESAREA];
    
    // Si el servicio original es uno de maternidad/parto/cesárea
    if (serviciosMaternidad.includes(servicioOriginal)) {
      // Solo permitir cambios entre servicios de maternidad
      if (!serviciosMaternidad.includes(servicioNuevo)) {
        setAlertConfig({
          type: "error",
          title: "¡Error!",
          message: "Solo se permite cambiar entre servicios de Maternidad, Parto y Cesárea. Cualquier otro cambio debe gestionarse por CAC/Call Center.",
          onConfirm: () => setShowAlert(false),
        });
        setShowAlert(true);
        return;
      }

      // Si es cambio entre servicios de maternidad, mostrar confirmación
      setAlertConfig({
        type: "confirm",
        title: "¡Atención!",
        message: "¿Estás seguro de que deseas cambiar el servicio?",
        confirmText: "Sí",
        cancelText: "No",
        onConfirm: () => {
          setShowAlert(false);
          
          // Si confirma, actualizar el estado local y llamar al backend
          setTipoServicioSelected(nuevoValor);

          if (typeof cambiarServicio === "function") {
            const poliza = encabezado?.poliza || "";
            const polizaParts = poliza.split("-");
            const companiaPoliza = polizaParts.length >= 1 ? Number(polizaParts[0]) : 0;
            const ramoPoliza = polizaParts.length >= 2 ? Number(polizaParts[1]) : 0;

            const numeroIngreso = encabezado?.numeroIngreso || "";
            const [anoReporte, secuencialReporte] = numeroIngreso.split("-");

            const payload = {
              anoReporte: Number(anoReporte) || 0,
              companiaPoliza,
              ramoPoliza,
              secuencialReporte: Number(secuencialReporte) || 0,
              servicioVigente: Number(servicioNuevo),
            };

            cambiarServicio(payload);
          }
        },
        onCancel: () => {
          setShowAlert(false);
          // Si cancela, no hacer nada (mantener el servicio original)
        },
      });
      setShowAlert(true);
      return;
    }

    // Si pasa todas las validaciones, actualizar el estado local
    setTipoServicioSelected(nuevoValor);

    // Llamar al backend para guardar el cambio de servicio
    if (typeof cambiarServicio === "function") {
      const poliza = encabezado?.poliza || "";
      const polizaParts = poliza.split("-");
      const companiaPoliza = polizaParts.length >= 1 ? Number(polizaParts[0]) : 0;
      const ramoPoliza = polizaParts.length >= 2 ? Number(polizaParts[1]) : 0;

      const numeroIngreso = encabezado?.numeroIngreso || "";
      const [anoReporte, secuencialReporte] = numeroIngreso.split("-");

      const payload = {
        anoReporte: Number(anoReporte) || 0,
        companiaPoliza,
        ramoPoliza,
        secuencialReporte: Number(secuencialReporte) || 0,
        servicioVigente: Number(servicioNuevo),
      };

      cambiarServicio(payload);
    }
  };

  const handleFechaEgresoChange = (nuevaFecha) => {
    if (!nuevaFecha) {
      setFechaEgresoSelected("");
      return;
    }

    setFechaEgresoSelected(nuevaFecha);
  };

  const handleFechaEgresoBlur = () => {
    const nuevaFecha = fechaEgresoSelected;

    if (!nuevaFecha || nuevaFecha.length !== 10) {
      return;
    }

    const fechaParseada = new Date(`${nuevaFecha}T00:00:00`);
    if (isNaN(fechaParseada.getTime())) {
      return;
    }

    // Validación: la fecha de egreso no puede ser futura
    const fechaActualParaValidar = new Date();
    fechaActualParaValidar.setHours(0, 0, 0, 0); // Normalizar a medianoche
    if (fechaParseada > fechaActualParaValidar) {
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: "La fecha de egreso no puede ser una fecha futura. Por favor, seleccione la fecha actual o una fecha anterior.",
        onConfirm: () => {
          setShowAlert(false);
          setFechaEgresoSelected("");
        },
      });
      setShowAlert(true);
      return;
    }

    if (fechaIngresoValue) {
      const fechaServicio = new Date(`${fechaIngresoValue}T00:00:00`);
      if (fechaParseada < fechaServicio) {
        setAlertConfig({
          type: "info",
          title: "¡Atención!",
          message: "La fecha de egreso no puede ser menor a la fecha de servicio.",
          onConfirm: () => {
            setShowAlert(false);
            setFechaEgresoSelected("");
          },
        });
        setShowAlert(true);
        return;
      }

      // Validación para servicio ambulatorio: las fechas deben ser iguales (mismo día)
      if (tipoServicioSelected === SERVICIO_AMBULATORIO) {
        // Comparar las fechas directamente como strings (formato YYYY-MM-DD)
        if (fechaIngresoValue !== nuevaFecha) {
          setAlertConfig({
            type: "error",
            title: "¡Error!",
            message: "El paciente supera límites de días autorizados por este canal; gestionar por CAC/Call Center.",
            onConfirm: () => {
              setShowAlert(false);
              setFormularioBloqueado(true);
              setFechaEgresoSelected("");
            },
          });
          setShowAlert(true);
          return;
        }
      }

      // Validación para servicio hospitalización: la fecha de egreso no puede ser mayor a diasHospitalarios días
      if (tipoServicioSelected === SERVICIO_HOSPITALIZACION) {
        const diferenciaMs = fechaParseada - fechaServicio;
        const diferenciaDias = Math.floor(diferenciaMs / (1000 * 60 * 60 * 24));

        if (diferenciaDias > diasHospitalarios) {
          // Primero mostrar alerta de confirmación
          setAlertConfig({
            type: "confirm",
            title: "¡Atención!",
            message: "¿Estás seguro de que deseas continuar?",
            confirmText: "Sí",
            cancelText: "No",
            onConfirm: () => {
              setShowAlert(false);
              
              // Si confirma, mostrar la alerta de error y bloquear el formulario
              setTimeout(() => {
                setAlertConfig({
                  type: "error",
                  title: "¡Error!",
                  message: `El internamiento supera los ${diasHospitalarios} días, por lo que el proceso de alta no puede gestionarse a través de WebSalud. Para seguimiento y validación de cobertura, comuníquese con nuestro Centro de Atención al Cliente.`,
                  onConfirm: () => {
                    setShowAlert(false);
                    setFormularioBloqueado(true);
                    setFechaEgresoSelected("");
                  },
                });
                setShowAlert(true);
              }, 100);
            },
            onCancel: () => {
              setShowAlert(false);
              // Si cancela, revertir la fecha de egreso para que pueda colocar otra
              setFechaEgresoSelected(fechaSalidaValue || "");
            },
          });
          setShowAlert(true);
          return;
        }
      }

      // Validación para servicio maternidad, parto y cesárea: la fecha de egreso no puede ser mayor a diasHospitalarios días
      if (tipoServicioSelected === SERVICIO_MATERNIDAD || 
          tipoServicioSelected === SERVICIO_PARTO || 
          tipoServicioSelected === SERVICIO_CESAREA) {
        const diferenciaMs = fechaParseada - fechaServicio;
        const diferenciaDias = Math.floor(diferenciaMs / (1000 * 60 * 60 * 24));

        if (diferenciaDias > diasHospitalarios) {
          // Primero mostrar alerta de confirmación
          setAlertConfig({
            type: "confirm",
            title: "¡Atención!",
            message: "¿Estás seguro de que deseas continuar?",
            confirmText: "Sí",
            cancelText: "No",
            onConfirm: () => {
              setShowAlert(false);
              
              // Si confirma, mostrar la alerta de error y bloquear el formulario
              setTimeout(() => {
                setAlertConfig({
                  type: "error",
                  title: "¡Error!",
                  message: `El internamiento supera los ${diasHospitalarios} días, por lo que el proceso de alta no puede gestionarse a través de WebSalud. Para seguimiento y validación de cobertura, comuníquese con nuestro Centro de Atención al Cliente.`,
                  onConfirm: () => {
                    setShowAlert(false);
                    setFormularioBloqueado(true);
                    setFechaEgresoSelected("");
                  },
                });
                setShowAlert(true);
              }, 100);
            },
            onCancel: () => {
              setShowAlert(false);
              // Si cancela, revertir la fecha de egreso para que pueda colocar otra
              setFechaEgresoSelected(fechaSalidaValue || "");
            },
          });
          setShowAlert(true);
          return;
        }
      }
    }

    // Validación: la fecha de egreso no puede tener más de diasRegistroEgresos días de antigüedad respecto a la fecha actual
    const fechaActual = new Date();
    fechaActual.setHours(0, 0, 0, 0); // Normalizar a medianoche para comparar solo fechas
    const diferenciaConHoyMs = fechaActual - fechaParseada;
    const diasDeAntiguedad = Math.floor(diferenciaConHoyMs / (1000 * 60 * 60 * 24));

    if (diasDeAntiguedad > diasRegistroEgresos) {
      setAlertConfig({
        type: "warning",
        title: "¡Atención!",
        message: `No es posible registrar la de alta, ya que la fecha de egreso excede los ${diasRegistroEgresos} dias calendarios respecto a la fecha de registro. Para continuar con el proceso, debe comunicarse con nuestro Centro de atención al Cliente.`,
        onConfirm: () => {
          setShowAlert(false);
          setFechaEgresoSelected("");
        },
      });
      setShowAlert(true);
      return;
    }

    if (nuevaFecha === fechaSalidaValue) {
      return;
    }

    const poliza = encabezado?.poliza || "";
    const polizaParts = poliza.split("-");
    const compania = polizaParts.length >= 1 ? Number(polizaParts[0]) : 0;

    const numeroPlastico = encabezado?.numeroPlastico || 0;

    const fechaVersionISO = fechaParseada.toISOString();

    const payload = {
      compania,
      numeroPlastico: Number(numeroPlastico),
      fechaVersion: fechaVersionISO,
    };

    setPendingFechaEgreso(nuevaFecha);
    setEsperandoValidacionAfiliado(true);

    if (typeof validarAfiliadoVigente === "function") {
      validarAfiliadoVigente(payload);
    }
  };

  const tipoServicioOptions = (() => {
    const base = Array.isArray(dataTipoServicioCierreCuentas)
      ? dataTipoServicioCierreCuentas
      : [];

    const opciones = base.map((opt) => ({
      ...opt,
      disabled: String(opt.value) === SERVICIO_AMBULATORIO && !esIngresoAmbulatorio,
    }));

    if (!codigoServicioValue) return opciones;

    const existe = opciones.some((opt) => String(opt.value) === codigoServicioValue);
    if (existe) return opciones;

    return [
      ...opciones,
      {
        value: codigoServicioValue,
        label: encabezado?.descripcionServicio || codigoServicioValue,
        disabled: false,
      },
    ];
  })();

  if (!encabezado) return null;

  return (
    <FormWrapper
      maxWidth={"100%"}
      borderColor={empresa?.COLOR_PRINCIPAL}
      style={{ width: "100%" }}
    >
      <AutorizacionForm>
        <AutorizacionFormGroup>
          <CustomInput
            type={"text"}
            width={"100%"}
            placeholder={"Número de ingreso"}
            label={"Número de ingreso"}
            value={encabezado.numeroIngreso || ""}
            disabled={true}
          />
        </AutorizacionFormGroup>

        <AutorizacionFormGroup>
          <CustomSelect
            width={"100%"}
            options={tipoServicioOptions}
            label={"Tipo de Servicio"}
            value={tipoServicioSelected}
            required={true}
            showRequired={false}
            showDefaultOption={true}
            disabled={isConvertido || isPdssNoValido || formularioBloqueado || formularioBloqueadoPorHojaQX}
            onChange={handleTipoServicioChange}
            errorMessage={erroresValidacion.tipoServicio}
          />
        </AutorizacionFormGroup>

        <AutorizacionFormGroup>
          <CustomInput
            type={"date"}
            width={"100%"}
            placeholder={"Fecha servicio"}
            label={"Fecha servicio"}
            value={fechaIngresoValue}
            disabled={true}
          />
        </AutorizacionFormGroup>

        <AutorizacionFormGroup>
          <CustomSelect
            width={"100%"}
            options={causaOptions}
            showDefaultOption={false}
            label={"Causa de internamiento"}
            value={causaInternamientoRaw}
            disabled={true}
          />
        </AutorizacionFormGroup>

        <AutorizacionFormGroup>
          <CustomSelect
            width={"100%"}
            options={motivoOptions}
            showDefaultOption={true}
            label={"Motivo Egreso"}
            value={currentMotivoEgreso}
            disabled={isConvertido || isPdssNoValido || formularioBloqueado || formularioBloqueadoPorHojaQX}
            onChange={handleMotivoEgresoSelect}
          />
        </AutorizacionFormGroup>

        <AutorizacionFormGroup>
          <CustomInput
            type={"date"}
            width={"100%"}
            placeholder={"Fecha egreso"}
            label={"Fecha egreso"}
            value={fechaEgresoSelected}
            max={fechaMaximaEgreso}
            disabled={isConvertido || isPdssNoValido || formularioBloqueado || formularioBloqueadoPorHojaQX || validarAfiliadoVigenteLoading}
            onChange={handleFechaEgresoChange}
            onBlur={handleFechaEgresoBlur}
            errorMessage={erroresValidacion.fechaEgreso}
          />
        </AutorizacionFormGroup>
      </AutorizacionForm>

      <CheckRender allowed={showAlert}>
        <CustomAlert {...alertConfig} />
      </CheckRender>
    </FormWrapper>
  );
};

export default AutorizacionIngresoDetalle;
